import { Controller } from '@hotwired/stimulus';
import Modal from 'bootstrap/js/dist/modal';

declare global {
  interface DocumentEventMap {
    'turbo:submit-end': TurboEvent;
  }
}

export default class ModalController extends Controller {
  private modal: Modal | undefined;

  public connect(): void {
    this.modal = Modal.getOrCreateInstance(this.element);

    if (this.element.classList.contains('show')) {
      this.modal.show();
    }

    document.addEventListener('turbo:submit-end', this._handleSubmit);
    this.element.addEventListener('hidden.bs.modal', this._navigateOnClose);
    this.element.addEventListener(
      'shown.bs.modal',
      this._copyCssVars.bind(this)
    );
  }

  public disconnect(): void {
    this.element.removeEventListener('hidden.bs.modal', this._navigateOnClose);
  }

  protected _copyCssVars(): void {
    const modal = this.element as HTMLElement;
    const modalBackdrop =
      document.querySelector<HTMLElement>('.modal-backdrop');
    const modalStyles = getComputedStyle(modal);
    const modalVars = Array.from(modal.style).filter(prop =>
      prop.startsWith('--bs')
    );

    modalVars.forEach(varName => {
      const varValue = modalStyles.getPropertyValue(varName);

      modalBackdrop?.style.setProperty(varName, varValue);
    });
  }

  protected _close(): void {
    const element = this.element;

    this.modal?.hide();

    element.addEventListener(
      'hidden.bs.modal',
      () => {
        this.modal?.dispose();
      },
      { once: true }
    );
  }

  private readonly _handleSubmit = (e: TurboEvent) => {
    if (e.detail.success) {
      this._close();
    }
  };

  private readonly _navigateOnClose = () => {
    const modal = this.element as HTMLElement;

    // During tests, we're often navigating so fast that
    // the modal ends up navigating after the redirect from the
    // server is already received, dropping us onto the wrong page.
    // In the future, it would be good if we could find a way to
    // see if we are already navigating.
    if (process.env.RAILS_ENV === 'test') {
      return;
    }

    // Note that if modalCloseUrl is set, this could override a redirect coming from the server
    if (!modal.dataset.modalCloseUrl) {
      return;
    }

    if (modal.dataset.modalCloseUrl === 'back') {
      window.history.back();

      return;
    }

    if (
      modal.dataset.modalCloseUrl ===
      `${window.location.pathname}${window.location.search}`
    ) {
      return;
    }

    const closeUrl = new URL(modal.dataset.modalCloseUrl, window.location.href);

    window.location.href = `${window.location.origin}${closeUrl.pathname}${closeUrl.search}${closeUrl.hash}`;
  };
}
